import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { Table } from '../../../shared/table/table';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { faArrowLeft, faEye, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Setup } from '../../shared/service/setup';
import { ToastService } from '../../../../core/shared/services/toast.service';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { GuarantorClientPayload } from '../../shared/models/guarantor.model';

interface GuarantorClient {
  stpGrntrClntSeq: number | null;
  clientSeq: number | null;
  clientName: string;
  guarantorAmount: number | null;
  riskCoverage: number | null;
  stsKey: number | null;
}

@Component({
  selector: 'app-guarantor-client-list',
  imports: [TranslatePipe, Table, Popup, FontAwesomeModule],
  templateUrl: './guarantor-client-list.html',
  styleUrl: './guarantor-client-list.css',
})
export class GuarantorClientList implements OnInit {
  showPopup = false;
  faPencil = faPencil;
  faEye = faEye;
  faTrash = faTrash;
  faArrowLeft = faArrowLeft;
  popupTitle = '';
  isDeleteConfirm = false;
  isViewMode = false;
  deleteMessage = '';

  stpGrntrSeq: number = 0;
  clientOptions: { label: string; value: number }[] = [];

  values: GuarantorClient[] = [];
  selectedRow: GuarantorClient | null = null;
  loading = false;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    public router: Router,
    private setupService: Setup,
    private picklist: PickListService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.stpGrntrSeq = Number(params['stpGrntrSeq']);
      if (this.stpGrntrSeq) {
        this.loadValues();
      }
    });

    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildColumns();
    });

    this.loadClients();
  }

  loadClients() {
    this.setupService.getclientList().subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.clientOptions = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({ label: String(item.clntNm ?? '').trim(), value: Number(item.clientSeq) }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

        // Rows may have loaded first; fill in names they couldn't resolve then.
        this.values = this.values.map((row) => ({ ...row, clientName: row.clientName || this.getClientName(row.clientSeq) }));
      },
      error: (err) => {
        console.error('Failed to load clients', err);
      }
    });
  }

  loadValues() {
    this.loading = true;
    this.setupService.getGuarantorClientList(this.stpGrntrSeq).subscribe({
      next: (res: any) => {
        this.loading = false;
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.values = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            stpGrntrClntSeq: this.toNumber(item.stpGrntrClntSeq),
            clientSeq: this.toNumber(item.clntSeq),
            clientName: String(item.clntNm ?? '').trim() || this.getClientName(this.toNumber(item.clntSeq)),
            guarantorAmount: this.toNumber(item.grntrAmnt),
            riskCoverage: this.toNumber(item.rskCvrg),
            stsKey: this.toNumber(item.stsKey)
          }));
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load guarantor clients', err);
      }
    });
  }

  private getClientName(clientSeq: number | null): string {
    return this.clientOptions.find((option) => option.value === clientSeq)?.label ?? '';
  }

  private toNumber(value: unknown): number | null {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  popupFields: DynamicFieldConfig[] = [];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [];

  buildColumns(): void {
    this.columns = [
      { key: 'clientName', label: 'SETUP.GUARANTOR_CLIENT_LIST.CLIENT_NAME', type: 'text' },
      { key: 'guarantorAmount', label: 'SETUP.GUARANTOR_CLIENT_LIST.GUARANTOR_AMOUNT', type: 'currency' },
      { key: 'riskCoverage', label: 'SETUP.GUARANTOR_CLIENT_LIST.RISK_COVERAGE', type: 'currency' },
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: this.getStatusMap(),
        badgeMap: this.getStatusBadgeMap()
      }
    ];
  }

  getStatusMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((option) => {
      map[option.value] = option.label;
    });

    return map;
  }

  getStatusBadgeMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    // Rows always carry 706 (active) / 707 (inactive), so the pill colour must not depend on the picklist label text.
    const map: Record<number, string> = { 706: 'status-active', 707: 'status-inactive' };

    options.forEach((option) => {
      const label = option.label?.trim().toLowerCase();

      if (label === 'active') {
        map[option.value] = 'status-active';
      } else if (label === 'inactive') {
        map[option.value] = 'status-inactive';
      } else if (!map[option.value]) {
        map[option.value] = 'status-default';
      }
    });

    return map;
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  private buildFields(row: GuarantorClient | null, required: boolean): DynamicFieldConfig[] {
    return [
      {
        label: 'SETUP.GUARANTOR_CLIENT_LIST.CLIENT_NAME',
        key: 'clientSeq',
        type: 'select',
        value: row?.clientSeq ?? null,
        options$: this.clientOptions,
        searchable: true,
        validations: required ? { required: true } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_CLIENT_LIST.GUARANTOR_AMOUNT',
        key: 'guarantorAmount',
        type: 'text',
        value: row?.guarantorAmount ?? '',
        validations: required ? { required: true, numbersOnly: true } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_CLIENT_LIST.RISK_COVERAGE',
        key: 'riskCoverage',
        type: 'text',
        value: row?.riskCoverage ?? '',
        maxlength: 2,
        validations: required ? { required: true, numbersOnly: true, maxLength: 2 } : undefined
      },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'select',
        value: row?.stsKey ?? this.getDefaultStatus(),
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: required ? { required: true } : undefined
      }
    ];
  }

  openGuarantorClient() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.GUARANTOR_CLIENT_LIST.ADD_CLIENT_GUARANTOR';
    this.isDeleteConfirm = false;
    this.isViewMode = false;
    this.popupFields = this.buildFields(null, true);
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_CLIENT_LIST.EDIT_CLIENT_GUARANTOR';
        this.isDeleteConfirm = false;
        this.isViewMode = false;
        this.popupFields = this.buildFields(row, true);
        this.showPopup = true;
      }
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_CLIENT_LIST.VIEW_CLIENT_GUARANTOR';
        this.isDeleteConfirm = false;
        this.isViewMode = true;
        this.popupFields = this.buildFields(row, false);
        this.showPopup = true;
      }
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_CLIENT_LIST.DELETE_CLIENT_GUARANTOR';
        this.isDeleteConfirm = true;
        this.isViewMode = false;
        this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.clientName });
        this.showPopup = true;
      }
    }
  ];

  /** Returns false so the popup only closes once the API succeeds. */
  SaveGuarantorClient(formData: any): boolean {
    const payload = this.createPayload(formData);

    this.setupService.saveGuarantorClient(payload).subscribe({
      next: () => {
        this.toast.success('SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadValues();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('SETUP.FAILED_TO_SAVE_RECORD');
      }
    });

    return false;
  }

  onDeleteConfirmed(): void {
    const payload = this.createPayload({});

    this.setupService.saveGuarantorClient(payload).subscribe({
      next: () => {
        this.toast.success('SETUP.DELETED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadValues();
      },
      error: (err) => {
        console.error('Delete failed', err);
        this.toast.error('SETUP.FAILED_TO_DELETE_RECORD');
      }
    });
  }

  private createPayload(formData: any): GuarantorClientPayload {
    const isDelete = this.isDeleteConfirm;
    const row = this.selectedRow;

    // If Delete, use existing values. If Edit/Create, use Form values.
    return {
      stpGrntrClntSeq: row?.stpGrntrClntSeq ?? null,
      stpGrntrSeq: this.stpGrntrSeq,
      clntSeq: Number((isDelete && row) ? row.clientSeq : formData.clientSeq),
      grntrAmnt: Number((isDelete && row) ? row.guarantorAmount : formData.guarantorAmount),
      rskCvrg: Number((isDelete && row) ? row.riskCoverage : formData.riskCoverage),
      stsKey: Number((isDelete && row) ? row.stsKey : formData.stsKey),
      delFlg: isDelete ? 1 : 0
    };
  }
}
